/**
 * Practical-task renderers for Module 2 lesson 5:
 *   triage - classify a realistic message as genuine / suspicious / scam
 *   audit  - work through a real device checklist, saved to the server
 *
 * `audit` follows the same contract as `design` in ./activities.jsx:
 * it receives `onSave` and `saved` from the lesson player.
 */
import { useState, useEffect, useMemo } from 'react';
import CubMascot from '../components/CubMascot.jsx';

/* ============================================================
   triage
   ============================================================ */
export function TriagePage({ page, onComplete }) {
  const [index, setIndex] = useState(0);
  const [answered, setAnswered] = useState({});
  const [picked, setPicked] = useState(null);

  const current = page.messages[index];
  const isLast = index === page.messages.length - 1;
  const done = Object.keys(answered).length === page.messages.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const choose = (verdictId) => {
    if (picked) return;
    setPicked(verdictId);
    setAnswered((a) => ({ ...a, [current.id]: verdictId }));
  };

  const next = () => {
    setPicked(null);
    setIndex((i) => Math.min(i + 1, page.messages.length - 1));
  };

  const correct = picked === current.answer;
  const rightVerdict = page.verdicts.find((v) => v.id === current.answer);
  const score = page.messages.filter((m) => answered[m.id] === m.answer).length;

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="sort-progress">
        Message {index + 1} of {page.messages.length} · {score} correctly identified
      </div>

      {/* ---------- the message ---------- */}
      <article className="msg-card anim-pop" key={current.id}>
        <header className="msg-head">
          <span className="msg-icon" aria-hidden="true">{current.icon}</span>
          <div className="grow">
            <span className="msg-channel">{current.channel}</span>
            <div className="msg-from">{current.from}</div>
            {current.subject && <div className="msg-subject">{current.subject}</div>}
          </div>
        </header>
        <div className="msg-body">
          {current.body.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      </article>

      {/* ---------- the verdict ---------- */}
      <div className="verdict-row">
        {page.verdicts.map((v) => {
          const isPicked = picked === v.id;
          const isAnswer = picked && v.id === current.answer;
          return (
            <button
              key={v.id}
              type="button"
              className={`verdict t-${v.color} ${isPicked ? 'is-picked' : ''} ${isAnswer ? 'is-answer' : ''}`}
              onClick={() => choose(v.id)}
              disabled={Boolean(picked)}
              aria-pressed={isPicked}
            >
              <span className="verdict-icon" aria-hidden="true">{v.icon}</span>
              <span>{v.label}</span>
            </button>
          );
        })}
      </div>

      {picked && (
        <>
          <div className={`feedback ${correct ? 'good' : 'neutral'} anim-pop`}>
            <CubMascot
              avatar={correct ? 'cub-mint' : 'cub-sky'}
              mood={correct ? 'cheer' : 'think'}
              size={72}
            />
            <div>
              <strong>
                {correct ? 'Correct, detective!' : `Actually this one is: ${rightVerdict?.label}`}
              </strong>
              <p>{current.why}</p>

              {current.flags?.length > 0 && (
                <div className="flag-row">
                  <span className="small faint">Evidence:</span>
                  {current.flags.map((f) => (
                    <span key={f} className="flag-chip">🚩 {f}</span>
                  ))}
                </div>
              )}
              {current.flags?.length === 0 && (
                <div className="flag-row">
                  <span className="flag-chip is-clean">✅ No red flags found</span>
                </div>
              )}
            </div>
          </div>

          {!isLast && (
            <div className="row center mt-md">
              <button type="button" className="btn" onClick={next}>
                Next message →
              </button>
            </div>
          )}
        </>
      )}

      {done && page.debrief && (
        <div className="feedback good anim-pop">
          <span className="feedback-emoji" aria-hidden="true">🕵️</span>
          <div>
            <strong>
              Mission one complete — {score} of {page.messages.length} correct!
            </strong>
            <p>{page.debrief}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   audit
   ============================================================ */
const ANSWERS = [
  { id: 'yes', label: 'Yes', icon: '✅', color: 'mint' },
  { id: 'no', label: 'No', icon: '❌', color: 'coral' },
  { id: 'unsure', label: 'Not sure', icon: '❓', color: 'sun' },
];

export function AuditPage({ page, onComplete, onSave, saved }) {
  const allItems = useMemo(
    () => page.sections.flatMap((s) => s.items.map((i) => ({ ...i, sectionId: s.id }))),
    [page.sections],
  );

  const [answers, setAnswers] = useState(() =>
    Object.fromEntries((saved ?? []).map((a) => [a.id, a.answer])),
  );
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const answeredCount = allItems.filter((i) => answers[i.id]).length;
  const complete = answeredCount === allItems.length;

  useEffect(() => {
    if (complete) onComplete();
  }, [complete, onComplete]);

  const set = (itemId, value) => {
    setAnswers((a) => ({ ...a, [itemId]: value }));
    setStatus('');
  };

  const secure = allItems.filter((i) => answers[i.id] === 'yes').length;
  const todo = allItems.filter((i) => answers[i.id] === 'no');
  const check = allItems.filter((i) => answers[i.id] === 'unsure');

  const save = async () => {
    setBusy(true);
    setStatus('');
    try {
      await onSave(allItems.filter((i) => answers[i.id]).map((i) => ({ id: i.id, answer: answers[i.id] })));
      setStatus('saved');
    } catch (err) {
      setStatus(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>
      {page.note && (
        <div className="alert alert-info">
          <span className="alert-icon" aria-hidden="true">ℹ️</span>
          <div className="grow">{page.note}</div>
        </div>
      )}

      <div className="sort-progress">
        {answeredCount} of {allItems.length} checks answered
      </div>

      {page.sections.map((section) => (
        <section key={section.id} className="audit-section">
          <h3>
            <span aria-hidden="true">{section.icon}</span> {section.title}
          </h3>

          {section.items.map((item) => (
            <div key={item.id} className={`audit-item ${answers[item.id] ? 'is-answered' : ''}`}>
              <div className="audit-question">
                <strong>{item.label}</strong>
                <p className="small muted">{item.why}</p>
              </div>
              <div className="audit-answers" role="group" aria-label={item.label}>
                {ANSWERS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`audit-btn t-${a.color} ${answers[item.id] === a.id ? 'is-picked' : ''}`}
                    onClick={() => set(item.id, a.id)}
                    aria-pressed={answers[item.id] === a.id}
                  >
                    <span aria-hidden="true">{a.icon}</span> {a.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}

      {/* ---------- the report ---------- */}
      {answeredCount > 0 && (
        <div className="audit-report">
          <h3>📋 Your audit report</h3>

          <div className="audit-score">
            <div className="audit-score-num">
              {secure}/{allItems.length}
            </div>
            <div>
              <strong>checks already secure</strong>
              <p className="small muted">
                {todo.length + check.length === 0
                  ? 'Everything is set up. Brilliant work!'
                  : `${todo.length} to fix, ${check.length} to go and look at.`}
              </p>
            </div>
          </div>

          {todo.length > 0 && (
            <div className="audit-todo">
              <strong>🔧 To fix, with a grown-up:</strong>
              <ul>
                {todo.map((i) => (
                  <li key={i.id}>{i.label}</li>
                ))}
              </ul>
            </div>
          )}

          {check.length > 0 && (
            <div className="audit-todo is-check">
              <strong>🔎 To go and check:</strong>
              <ul>
                {check.map((i) => (
                  <li key={i.id}>{i.label}</li>
                ))}
              </ul>
            </div>
          )}

          {complete && (
            <div className="feedback good">
              <span className="feedback-emoji" aria-hidden="true">🕵️</span>
              <div>
                <strong>Audit finished!</strong>
                <p>{page.successText}</p>
              </div>
            </div>
          )}

          <div className="row center mt-md">
            <button
              type="button"
              className="btn btn-lg"
              onClick={save}
              disabled={busy || answeredCount === 0}
            >
              {busy ? 'Saving…' : '💾 Save my audit'}
            </button>
          </div>

          {status === 'saved' && (
            <p className="tc mt-md save-ok">✓ Saved! Your teacher can see your audit now.</p>
          )}
          {status && status !== 'saved' && <p className="tc mt-md save-bad">{status}</p>}
        </div>
      )}
    </div>
  );
}

/** Compact read-only view of a saved audit, used in the admin reports. */
export function AuditSummary({ items }) {
  const counts = items.reduce(
    (acc, i) => ({ ...acc, [i.answer]: (acc[i.answer] ?? 0) + 1 }),
    {},
  );
  return (
    <div className="audit-summary">
      <span className="pill pill-good">✅ {counts.yes ?? 0} secure</span>
      <span className="pill pill-bad">❌ {counts.no ?? 0} to fix</span>
      <span className="pill pill-warn">❓ {counts.unsure ?? 0} to check</span>
      <span className="pill">{items.length} checks answered</span>
    </div>
  );
}
